import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ReleasePackage } from "interface/package";
import { GrideDateFormatter } from "ui/Date";
import { ICON_NAMES } from "utils/constant";

type ReleasePackageItem = ReleasePackage & Record<string, any>;

interface ReleasedPackagesMobileListProps {
	items: ReleasePackageItem[];
	loading: boolean;
	onOpenPdf: (item: ReleasePackage) => void;
	onLoadMore: () => void;
}

const getValue = <T,>(item: ReleasePackageItem, camelKey: string, pascalKey: string): T | undefined => {
	return (item[camelKey] ?? item[pascalKey]) as T | undefined;
};

const formatAmount = (value?: number | string | null) => {
	const asNumber = Number(value || 0);
	return asNumber.toLocaleString("no-NO", {
		maximumFractionDigits: 2,
		style: "currency",
		currency: "NOK",
	});
};

const formatWeight = (value?: number | string | null) => {
	const asNumber = Number(value || 0);
	return `${asNumber.toLocaleString("no-NO", {
		maximumFractionDigits: 2,
	})} kg`;
};

const getStatusClassName = (item: ReleasePackageItem) => {
	const signedByName = getValue<string>(item, "signedByName", "SignedByName");
	const salesConfirmationStatusCode = getValue<string>(
		item,
		"salesConfirmationStatusCode",
		"SalesConfirmationStatusCode",
	);

	if (!signedByName) {
		return "released-package-list-card--status-red";
	}

	if (!salesConfirmationStatusCode) {
		return "released-package-list-card--status-yellow";
	}

	return "released-package-list-card--status-green";
};

const ReleasedPackagesMobileList: FC<ReleasedPackagesMobileListProps> = ({
	items,
	loading,
	onOpenPdf,
	onLoadMore,
}) => {
	const { t } = useTranslation();
	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
		if (remaining < 200) {
			onLoadMore();
		}
	};

	return (
		<div className="released-package-list-mobile" onScroll={handleScroll}>
			{empty && (
				<div className="released-package-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item, itemIndex) => {
				const registrationId = getValue<number | string>(
					item,
					"registrationId",
					"RegistrationId",
				);
				const packageId = getValue<number | string>(item, "packageId", "PackageId");
				const confirmedTime = getValue<Date | string>(
					item,
					"confirmedTime",
					"ConfirmedTime",
				);
				const registeredTime = getValue<Date | string>(
					item,
					"registeredTime",
					"RegisteredTime",
				);
				const vesselRegistrationId = getValue<string>(
					item,
					"vesselRegistrationId",
					"VesselRegistrationId",
				);
				const vesselName = getValue<string>(item, "vesselName", "VesselName");
				const buyerName = getValue<string>(item, "buyerName", "BuyerName");
				const terminalName = getValue<string>(item, "terminalName", "TerminalName");
				const salesType = getValue<string>(item, "salesType", "SalesType");
				const signedByName = getValue<string>(item, "signedByName", "SignedByName");
				const liveWeightKg = getValue<number | string>(
					item,
					"liveWeightKg",
					"LiveWeightKg",
				);
				const amount = getValue<number | string>(item, "amount", "Amount");
				const reportGuid = getValue<string>(item, "reportGuid", "ReportGuid");
				const itemKey = `${registrationId ?? "noreg"}-${packageId ?? "nopackage"}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`released-package-list-card ${getStatusClassName(item)}`}
					>
						<div className="released-package-list-card-header">
							<div className="released-package-list-card-note">{registrationId || "-"}</div>
							<div className="released-package-list-card-vessel">{vesselName || "-"}</div>
							<div className="released-package-list-card-date">
								{confirmedTime ? (
									<GrideDateFormatter
										date={confirmedTime}
										formatType="dd.MM.yyyy"
										noTd={true}
									/>
								) : (
									"-"
								)}
							</div>
						</div>

						<div className="released-package-list-card-body">
							<div>
								<span>{t("Package")}</span>
								<strong>{packageId || "-"}</strong>
							</div>
							<div>
								<span>{t("BuyerName")}</span>
								<strong>{buyerName || "-"}</strong>
							</div>
							<div>
								<span>{t("VesselRegistrationId")}</span>
								<strong>{vesselRegistrationId || "-"}</strong>
							</div>
							<div>
								<span>{t("Type")}</span>
								<strong>{salesType || "-"}</strong>
							</div>
							<div>
								<span>Innm.dato</span>
								<strong>
									{registeredTime ? (
										<GrideDateFormatter
											date={registeredTime}
											formatType="dd.MM.yyyy"
											noTd={true}
										/>
									) : (
										"-"
									)}
								</strong>
							</div>
							<div>
								<span>{t("Terminal")}</span>
								<strong>{terminalName || "-"}</strong>
							</div>
							<div>
								<span>{t("Weight")}</span>
								<strong>{formatWeight(liveWeightKg)}</strong>
							</div>
							<div>
								<span>{t("Value")}</span>
								<strong>{formatAmount(amount)}</strong>
							</div>
							<div className="released-package-list-card-text-block">
								<span>Signert av</span>
								<strong>{signedByName || "-"}</strong>
							</div>
						</div>

						<div className="released-package-list-card-actions">
							<button
								type="button"
								className="released-package-list-icon-btn"
								onClick={() => onOpenPdf({ ...item, reportGuid } as ReleasePackage)}
								title={t("Download")}
								disabled={!reportGuid}
							>
								<img
									src={`/icon/${ICON_NAMES.Download}.svg`}
									alt={t("Download")}
								/>
							</button>
						</div>
					</div>
				);
			})}

			{loading && (
				<div className="released-package-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default ReleasedPackagesMobileList;